import React, { useEffect, useState } from "react";
import { removeIntegration as removeIntegrationService, getIntegrationSources, connectIntegration as connectIntegrationService } from "../../services/integrations";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import axiosInstance from "../../utils/api";

const styles = {
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1050,
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "600px",
    maxWidth: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
    padding: "20px",
  },
  addButton: {
    backgroundColor: "#dc3545",
    border: "none",
    color: "white",
  },
  integrationCard: {
    border: "1px solid #dee2e6",
    borderRadius: "8px",
    padding: "1.5rem",
    marginBottom: "1rem",
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    minHeight: "200px",
    display: "flex",
    flexDirection: "column",
  },
  connectButton: {
    padding: "0.375rem 1rem",
    fontSize: "0.875rem",
    borderRadius: "6px",
    border: "1px solid #000",
    transition: "all 0.2s",
    backgroundColor: "#fff",
    color: "#000",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    width: "130px"
  },

  verifyButton: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    width: "100%",
    padding: "0.5rem",
    borderRadius: "6px",
    marginTop: "1rem",
  },
  tokenInput: {
    width: "100%",
    padding: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #dee2e6",
    marginTop: "0.5rem",
  },
};

const Integrations = ({ companyId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [token, setToken] = useState("");
  const [shopHost, setShopHost] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [integrationToRemove, setIntegrationToRemove] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formFields, setFormFields] = useState({}); // Dynamic form fields state

  // Function to fetch and transform integration data
  const fetchIntegrationData = async () => {
    try {
      setLoading(true);
      // Fetch available integration sources
      const integrationSources = await getIntegrationSources();
      
      // Transform API data to component structure
      // Only show integrations where status === true
      const transformedIntegrations = integrationSources
        .filter(source => source.status === true)
        .map((source) => {
          // Use user_integration.authenticated to determine connection status
          // If user_integration exists and authenticated === true, show "Remove"
          // Otherwise, show "Connect"
          const isConnected = source.user_integration?.authenticated === true;
          
          return {
            id: source.id.toString(),
            name: source.name,
            logoUrl: source.logo_url, // Store logo_url directly
            description: source.description || '',
            status: isConnected ? 'connected' : 'disconnected',
            learnMore: source.learn_more || '#',
            apiId: source.user_integration?.id,
            sourceId: source.id,
            fields: source.fields || {},
            userIntegration: source.user_integration,
          };
        });
      
      setIntegrations(transformedIntegrations);
    } catch (error) {
      console.error('Failed to fetch integration data:', error);
      toast.error('Failed to load integrations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch integration sources from API
  useEffect(() => {
    fetchIntegrationData();
  }, []);

  const [availableIntegrations] = useState([
    {
      id: "mailchimp",
      name: "Mailchimp",
      icon: "/mailchimp.svg",
      description: "Automate email campaigns and track subscriber engagement",
    },
    {
      id: "hubspot",
      name: "HubSpot CRM",
      icon: "hubspot.svg",
      description:
        "Organize customer data, manage leads, and improve conversions",
    },
    {
      id: "salesforce",
      name: "Salesforce",
      icon: "/salesforce.svg",
      description:
        "Centralize customer insights, track sales, and improve CRM efficiency",
    },
  ]);

  const connectToGoogle = async () => {
    try {
      // Get the auth token from localStorage
      const authToken = localStorage.getItem('token');
      
      if (!authToken) {
        alert('No authentication token found. Please login again.');
        return false;
      }
      
      const response = await axiosInstance.post('/google/connect', {}, {
        headers: {
          'Authorization': authToken, 
        }
      });
      
      console.log('Google API response:', response);
      
      const result = response.data;
      
      // Check if API returned an auth_url for OAuth flow
      if (result.auth_url) {
        // Redirect to Google OAuth URL
        window.location.href = result.auth_url;
        return false; 
      }
      
      console.log('Google connection successful:', result);
      return true;
    } catch (error) {
      console.error('Google connection error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to connect to Google: ${errorMessage}`);
      return false;
    }
  };

  const handleTokenSubmit = async () => {
    // Handle special cases (Google Sheets OAuth)
    if (selectedIntegration?.name?.toLowerCase() === "google sheets") {
      const googleConnected = await connectToGoogle();
      if (!googleConnected) {
        return; 
      }
      // Refetch integration data after Google connection
      await fetchIntegrationData();
      setIsTokenModalOpen(false);
      return;
    }

    // Validate required fields
    if (!validateRequiredFields(selectedIntegration)) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Call API to connect integration with formFields data
    try {
      await connectIntegrationService(
        selectedIntegration.sourceId,
        selectedIntegration.name,
        formFields
      );
      
      // Refetch integration data to get updated status
      await fetchIntegrationData();
      
      toast.success(`${selectedIntegration.name} integration connected successfully`);
      setIsTokenModalOpen(false);
      setFormFields({});
    } catch (error) {
      console.error('Error connecting integration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to connect ${selectedIntegration.name} integration: ${errorMessage}`);
    }
  };

  const handleConnectClick = (integration) => {
    setSelectedIntegration(integration);
    
    // Initialize form fields based on integration fields
    const initialFields = {};
    const allFields = [
      ...(integration.fields?.required || []),
      ...(integration.fields?.optional || [])
    ];
    allFields.forEach(field => {
      initialFields[field] = '';
    });
    setFormFields(initialFields);
    
    setIsTokenModalOpen(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const validateRequiredFields = (integration) => {
    if (!integration?.fields?.required) return true;
    return integration.fields.required.every(field => 
      formFields[field] && formFields[field].trim() !== ''
    );
  };

  const handleDisconnect = (integrationItem) => {
    // Show confirmation modal
    setIntegrationToRemove(integrationItem);
    setShowRemoveConfirmModal(true);
  };

  const confirmRemoveIntegration = async () => {
    if (!integrationToRemove) {
      return;
    }

    setShowRemoveConfirmModal(false);

    // Check if we have the API ID
    const apiId = integrationToRemove.apiId;
    const integrationItem = integrationToRemove;
    
    setIntegrationToRemove(null);
    
    if (!apiId) {
      // If no API ID, just update local state (for backward compatibility)
      setIntegrations(
        integrations.map((integration) => {
          if (integration.id === integrationItem.id) {
            return {
              ...integration,
              status: "disconnected",
              shopHost: "",
              adminToken: "",
              token: "",
              clientId: "",
              clientSecret: "",
            };
          }
          return integration;
        })
      );
      toast.success(`${integrationItem.name} integration removed successfully`);
      return;
    }

    // Call API to remove integration
    try {
      await removeIntegrationService(apiId, integrationItem.name);
      
      // Refetch integration data to get updated status
      await fetchIntegrationData();
      
      toast.success(`${integrationItem.name} integration removed successfully`);
    } catch (error) {
      console.error('Error removing integration:', error);
      toast.error(`Failed to remove ${integrationItem.name} integration. Please try again.`);
    }
  };

  return (
    <div class="company-management-card">
      <div className="container">
        <div className="row mb-2">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="detailsHeading"><strong>Integrations</strong></span>
              </div>
            {/*  <button
                className="btn btn-danger d-flex align-items-center gap-2"
                onClick={() => setIsModalOpen(true)}
                style={styles.addButton}
              >
                <span>+</span> Add new
              </button>
              */}
            </div>
          </div>
        </div>
        <div className="separator" />
        {loading ? (
          <div className="text-center py-4">
            <p className="text-muted">Loading integrations...</p>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No integrations available.</p>
          </div>
        ) : (
          <div className="row">
            {integrations.map((integration) => (
              <div className="col-md-5 mb-4" key={integration.id}>
                <div style={styles.integrationCard}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex gap-3">
                      {integration.logoUrl && (
                        <img
                          src={integration.logoUrl}
                          alt={`${integration.name} logo`}
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                      <div>
                        <h3 className="h5 mb-2">{integration.name}</h3>
                        <p className="text-muted small mb-0">
                          {integration.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        integration.status === "connected"
                          ? handleDisconnect(integration)
                          : handleConnectClick(integration)
                      }
                      style={styles.connectButton}
                    >
                      {integration.status === "connected" ? "Remove" : "+ Connect"}
                    </button>
                  </div>
                  <hr />
                  <a
                    href={integration.learnMore}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted"
                    style={{ textDecoration: "none", fontSize: "0.875rem" }}
                  >
                    Learn more
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {isModalOpen && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title">Add integration tools</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="position-relative mb-4">
                  <Search
                    size={20}
                    style={{
                      position: "absolute",
                      left: "1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                    }}
                  />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search integration tools"
                    style={{ paddingLeft: "2.5rem" }}
                  />
                </div>

                {availableIntegrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="border rounded p-3 mb-3 d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex gap-3 align-items-center">
                      <img
                        src={integration.icon}
                        alt={`${integration.name} logo`}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "contain",
                        }}
                      />
                      <div>
                        <h6 className="mb-1">{integration.name}</h6>
                        <p className="text-muted small mb-0">
                          {integration.description}
                          {integration.status === "connected" && (
                            <>
                              <br />
                              {integration.id === "shopify" ? (
                                <>
                                  <strong>Host:</strong> {integration.shopHost}
                                </>
                              ) : (
                                <>
                                  <strong>Token:</strong> {integration.token}
                                </>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedIntegration(integration);
                        setIsTokenModalOpen(true);
                        setIsModalOpen(false);
                      }}
                      className="btn btn-link text-muted"
                      style={{ fontSize: "1.5rem" }}
                    >
                      +
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isTokenModalOpen && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title">
                  Connect {selectedIntegration?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setIsTokenModalOpen(false);
                    setFormFields({});
                    setToken("");
                    setShopHost("");
                    setAdminToken("");
                    setClientId("");
                    setClientSecret("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Show instructions if available */}
                {selectedIntegration?.fields?.instructions && selectedIntegration.fields.instructions.length > 0 && (
                  <div className="mb-3">
                    <h6 className="mb-2">Instructions:</h6>
                    <ol className="small text-muted" style={{ paddingLeft: "1.5rem" }}>
                      {selectedIntegration.fields.instructions.map((instruction, idx) => (
                        <li key={idx} className="mb-2">{instruction}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Special handling for Google Sheets (OAuth) */}
                {selectedIntegration?.name?.toLowerCase() === "google sheets" ? (
                  <div>
                    <p className="text-muted mb-3">
                      Click the button below to connect your Google account. You will be redirected to Google to authorize the connection.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Required Fields */}
                    {selectedIntegration?.fields?.required && selectedIntegration.fields.required.length > 0 && (
                      <div className="mb-3">
                        {selectedIntegration.fields.required.map((field) => (
                          <div key={field} className="mb-2">
                            <label className="form-label small mb-1" style={{ fontWeight: 500 }}>
                              {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} <span className="text-danger">*</span>
                            </label>
                            <input
                              type={field.toLowerCase().includes('secret') || field.toLowerCase().includes('password') || field.toLowerCase().includes('token') ? "password" : "text"}
                              className="form-control"
                              placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                              value={formFields[field] || ''}
                              onChange={(e) => handleFieldChange(field, e.target.value)}
                              style={styles.tokenInput}
                              required
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Optional Fields */}
                    {selectedIntegration?.fields?.optional && selectedIntegration.fields.optional.length > 0 && (
                      <div className="mb-3">
                        {selectedIntegration.fields.optional.map((field) => (
                          <div key={field} className="mb-2">
                            <label className="form-label small mb-1" style={{ fontWeight: 500 }}>
                              {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </label>
                            <input
                              type={field.toLowerCase().includes('secret') || field.toLowerCase().includes('password') || field.toLowerCase().includes('token') ? "password" : "text"}
                              className="form-control"
                              placeholder={`Enter ${field.replace(/_/g, ' ')} (optional)`}
                              value={formFields[field] || ''}
                              onChange={(e) => handleFieldChange(field, e.target.value)}
                              style={styles.tokenInput}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Show message if no fields defined */}
                    {(!selectedIntegration?.fields?.required || selectedIntegration.fields.required.length === 0) &&
                     (!selectedIntegration?.fields?.optional || selectedIntegration.fields.optional.length === 0) && (
                      <p className="text-muted mb-3">
                        No additional configuration required. Click the button below to connect.
                      </p>
                    )}
                  </>
                )}

                <button 
                  onClick={handleTokenSubmit} 
                  style={{
                    ...styles.verifyButton,
                    backgroundColor: selectedIntegration?.name?.toLowerCase() === "google sheets" 
                      ? "#dc3545" 
                      : validateRequiredFields(selectedIntegration) 
                        ? "#dc3545" 
                        : "#6c757d",
                    cursor: selectedIntegration?.name?.toLowerCase() === "google sheets"
                      ? "pointer"
                      : validateRequiredFields(selectedIntegration)
                        ? "pointer"
                        : "not-allowed"
                  }}
                  disabled={selectedIntegration?.name?.toLowerCase() !== "google sheets" && !validateRequiredFields(selectedIntegration)}
                >
                  {selectedIntegration?.name?.toLowerCase() === "google sheets" ? "Connect Google" : "Connect"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Remove Integration Confirmation Modal */}
      <Modal show={showRemoveConfirmModal} onHide={() => {
        setShowRemoveConfirmModal(false);
        setIntegrationToRemove(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove the {integrationToRemove?.name} integration? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowRemoveConfirmModal(false);
            setIntegrationToRemove(null);
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemoveIntegration}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Integrations;
